import React, { useState, useEffect, useCallback } from 'react';
import './TemporalEvolution.css';

const DIMENSION_META = {
  identity: { label: 'Identity', color: '#6dbc9a', icon: '🪞' },
  values_beliefs: { label: 'Values & Beliefs', color: '#a882d6', icon: '⚖️' },
  personality: { label: 'Personality', color: '#e8a87c', icon: '🎭' },
  relationships: { label: 'Relationships', color: '#85c1e9', icon: '💞' },
  life_story: { label: 'Life Story', color: '#f5b041', icon: '📖' },
  emotional_patterns: { label: 'Emotional Patterns', color: '#e74c6f', icon: '🌊' },
  goals_aspirations: { label: 'Goals', color: '#58d68d', icon: '🎯' },
  daily_life: { label: 'Daily Life', color: '#aab7c4', icon: '☀️' },
};

const ALL_DIMS = Object.keys(DIMENSION_META);

function TemporalEvolution({ isOpen, onClose, token }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [visibleDims, setVisibleDims] = useState(new Set(ALL_DIMS));
  const [hoveredPoint, setHoveredPoint] = useState(null);

  const apiBase = process.env.REACT_APP_API_URL || '';

  const fetchTimeline = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/soul_profile/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load timeline');
      const data = await res.json();
      setSnapshots(data.snapshots || []);
    } catch (err) {
      console.error('Timeline fetch error:', err);
    }
    setLoading(false);
  }, [token, apiBase]);

  useEffect(() => {
    if (isOpen) fetchTimeline();
  }, [isOpen, fetchTimeline]);

  if (!isOpen) return null;

  // Reverse to chronological order for chart
  const chronological = [...snapshots].reverse();

  const toggleDim = (dim) => {
    setVisibleDims(prev => {
      const next = new Set(prev);
      if (next.has(dim)) next.delete(dim);
      else next.add(dim);
      return next;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  // SVG chart dimensions
  const chartW = 700;
  const chartH = 300;
  const padL = 50, padR = 20, padT = 20, padB = 40;
  const plotW = chartW - padL - padR;
  const plotH = chartH - padT - padB;

  const getX = (i) => padL + (chronological.length > 1 ? (i / (chronological.length - 1)) * plotW : plotW / 2);
  const getY = (score) => padT + plotH - (score * plotH);

  const buildPath = (dim) => {
    const points = [];
    chronological.forEach((snap, i) => {
      const scores = snap.dimension_scores || {};
      const score = typeof scores === 'string' ? JSON.parse(scores) : scores;
      const val = score[dim];
      if (val !== undefined) {
        points.push({ x: getX(i), y: getY(val), val, idx: i });
      }
    });
    if (points.length < 2) return { path: '', points };
    const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
    return { path: d, points };
  };

  return (
    <div className="te-overlay" onClick={onClose}>
      <div className="te-panel" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="te-header">
          <div className="te-header-left">
            <span className="te-header-icon">📈</span>
            <h2 className="te-title">Soul Evolution</h2>
          </div>
          <button className="te-close" onClick={onClose}>✕</button>
        </div>

        <div className="te-body">
          {loading ? (
            <div className="te-loading">
              <div className="te-loading-orb" />
              <p>Loading your evolution timeline...</p>
            </div>
          ) : snapshots.length === 0 ? (
            <div className="te-empty">
              <div className="te-empty-icon">📸</div>
              <h3>No snapshots yet</h3>
              <p>Soul snapshots are automatically created each time your AI companion processes your memories. Keep having conversations to build your evolution timeline!</p>
            </div>
          ) : (
            <>
              {/* Dimension legend / toggles */}
              <div className="te-legend">
                {ALL_DIMS.map(dim => {
                  const meta = DIMENSION_META[dim];
                  const active = visibleDims.has(dim);
                  return (
                    <button
                      key={dim}
                      className={`te-legend-item ${active ? 'active' : 'inactive'}`}
                      style={{ '--dim-color': meta.color }}
                      onClick={() => toggleDim(dim)}
                    >
                      <span className="te-legend-dot" />
                      <span>{meta.icon} {meta.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* SVG Chart */}
              <div className="te-chart-wrap">
                <svg
                  viewBox={`0 0 ${chartW} ${chartH}`}
                  className="te-chart-svg"
                  preserveAspectRatio="xMidYMid meet"
                >
                  {/* Grid lines */}
                  {[0, 0.25, 0.5, 0.75, 1].map(v => (
                    <g key={v}>
                      <line
                        x1={padL} y1={getY(v)} x2={chartW - padR} y2={getY(v)}
                        stroke="rgba(255,255,255,0.08)" strokeDasharray="4,4"
                      />
                      <text x={padL - 8} y={getY(v) + 4} fill="rgba(255,255,255,0.35)" fontSize="11" textAnchor="end">
                        {Math.round(v * 100)}%
                      </text>
                    </g>
                  ))}

                  {/* X-axis date labels */}
                  {chronological.map((snap, i) => {
                    // Show max ~8 labels
                    const step = Math.max(1, Math.floor(chronological.length / 8));
                    if (i % step !== 0 && i !== chronological.length - 1) return null;
                    return (
                      <text
                        key={i} x={getX(i)} y={chartH - 5}
                        fill="rgba(255,255,255,0.35)" fontSize="10" textAnchor="middle"
                      >
                        {formatDate(snap.snapshot_date)}
                      </text>
                    );
                  })}

                  {/* Lines */}
                  {ALL_DIMS.filter(d => visibleDims.has(d)).map(dim => {
                    const { path, points } = buildPath(dim);
                    const color = DIMENSION_META[dim].color;
                    return (
                      <g key={dim}>
                        {path && (
                          <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" opacity="0.8" />
                        )}
                        {points.map((p, pi) => (
                          <circle
                            key={pi} cx={p.x} cy={p.y} r={
                              hoveredPoint && hoveredPoint.dim === dim && hoveredPoint.idx === p.idx ? 6 : 3.5
                            }
                            fill={color}
                            stroke="rgba(0,0,0,0.4)" strokeWidth="1"
                            style={{ cursor: 'pointer', transition: 'r 0.15s' }}
                            onMouseEnter={() => setHoveredPoint({ dim, idx: p.idx, val: p.val, x: p.x, y: p.y })}
                            onMouseLeave={() => setHoveredPoint(null)}
                            onClick={() => setSelectedSnapshot(chronological[p.idx])}
                          />
                        ))}
                      </g>
                    );
                  })}

                  {/* Tooltip */}
                  {hoveredPoint && (
                    <g>
                      <rect
                        x={Math.min(hoveredPoint.x + 10, chartW - 120)} y={Math.max(hoveredPoint.y - 30, 5)}
                        width="110" height="26" rx="6"
                        fill="rgba(18,25,23,0.92)" stroke="rgba(109,188,154,0.3)"
                      />
                      <text
                        x={Math.min(hoveredPoint.x + 16, chartW - 114)} y={Math.max(hoveredPoint.y - 12, 22)}
                        fill="white" fontSize="11"
                      >
                        {DIMENSION_META[hoveredPoint.dim]?.label}: {Math.round(hoveredPoint.val * 100) / 10}
                      </text>
                    </g>
                  )}
                </svg>
              </div>

              {/* Overall score trend */}
              <div className="te-overall-trend">
                <h3>Soul Depth Over Time</h3>
                <div className="te-overall-scores">
                  {chronological.map((snap, i) => (
                    <div
                      key={snap.id || i}
                      className={`te-overall-bar-wrap ${selectedSnapshot?.id === snap.id ? 'selected' : ''}`}
                      onClick={() => setSelectedSnapshot(snap)}
                      title={`${formatDate(snap.snapshot_date)}: Depth ${Math.round((snap.overall_score || 0) * 80)}`}
                    >
                      <div
                        className="te-overall-bar"
                        style={{ height: `${Math.max(4, (snap.overall_score || 0) * 100)}%` }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Snapshot Detail */}
              {selectedSnapshot && (
                <div className="te-snapshot-detail">
                  <div className="te-snap-header">
                    <h3>📸 Snapshot — {formatDateTime(selectedSnapshot.snapshot_date)}</h3>
                    <button className="te-snap-close" onClick={() => setSelectedSnapshot(null)}>✕</button>
                  </div>
                  <div className="te-snap-stats">
                    <div className="te-snap-stat">
                      <span className="te-snap-stat-val">{Math.round((selectedSnapshot.overall_score || 0) * 80)}</span>
                      <span className="te-snap-stat-label">Depth</span>
                    </div>
                    <div className="te-snap-stat">
                      <span className="te-snap-stat-val">{selectedSnapshot.memories_count || 0}</span>
                      <span className="te-snap-stat-label">Memories</span>
                    </div>
                    <div className="te-snap-stat">
                      <span className="te-snap-stat-val">{selectedSnapshot.extractions_count || 0}</span>
                      <span className="te-snap-stat-label">Traits</span>
                    </div>
                  </div>

                  {/* Dimension scores */}
                  <div className="te-snap-dims">
                    {ALL_DIMS.map(dim => {
                      const scores = selectedSnapshot.dimension_scores || {};
                      const parsed = typeof scores === 'string' ? JSON.parse(scores) : scores;
                      const val = parsed[dim];
                      if (val === undefined) return null;
                      const meta = DIMENSION_META[dim];
                      return (
                        <div key={dim} className="te-snap-dim-row">
                          <span className="te-snap-dim-icon">{meta.icon}</span>
                          <span className="te-snap-dim-name">{meta.label}</span>
                          <div className="te-snap-dim-bar-bg">
                            <div
                              className="te-snap-dim-bar-fill"
                              style={{ width: `${val * 100}%`, background: meta.color }}
                            />
                          </div>
                          <span className="te-snap-dim-pct">{Math.round(val * 10) / 10}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Changes */}
                  {selectedSnapshot.changes && selectedSnapshot.changes.length > 0 && (
                    <div className="te-snap-changes">
                      <h4>Changes Detected</h4>
                      {(typeof selectedSnapshot.changes === 'string'
                        ? JSON.parse(selectedSnapshot.changes)
                        : selectedSnapshot.changes
                      ).map((ch, i) => (
                        <div key={i} className={`te-change-item ${ch.direction}`}>
                          <span className="te-change-arrow">{ch.direction === 'up' ? '▲' : '▼'}</span>
                          <span className="te-change-dim">{DIMENSION_META[ch.dimension]?.label || ch.dimension}</span>
                          <span className="te-change-delta">
                            {ch.direction === 'up' ? '+' : ''}{Math.round(ch.delta * 100) / 10}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default TemporalEvolution;
